import React from 'react'
import { Dropdown, Modal } from 'antd'
import type { MenuProps } from 'antd'
import { MoreOutlined } from '@ant-design/icons'
import { BillingProfileRecord } from '../../api/dto/client-billing-profile.dto'
import { BillingProfileStatus } from './types'
// Row actions share the users table trigger; the bordered KebabButton in this
// folder is the toolbar one.
import { KebabButton } from '../UserManagement/UserManagement.styles'

interface BillingProfileRowActionsProps {
  record: BillingProfileRecord
  onEdit: (record: BillingProfileRecord) => void
  /** Restoring a company, which is confirmed here. */
  onToggleStatus: (record: BillingProfileRecord) => void
  /** Removing one, which has its own dialog naming what each recipient loses. */
  onRemove: (record: BillingProfileRecord) => void
}

/** The row's kebab menu: edit, and remove or restore. */
export const BillingProfileRowActions = ({
  record,
  onEdit,
  onToggleStatus,
  onRemove,
}: BillingProfileRowActionsProps) => {
  const isActive = record.status === BillingProfileStatus.Active

  const confirmRestore = () =>
    Modal.confirm({
      title: `Restore ${record.clientName}?`,
      content: 'This company starts receiving funding invoices again.',
      okText: 'Restore',
      cancelText: 'Cancel',
      centered: true,
      onOk: () => onToggleStatus(record),
    })

  const items: MenuProps['items'] = [
    // The row click does this too, which nothing on screen says.
    {
      key: 'edit',
      label: 'Edit',
      onClick: () => onEdit(record),
    },
    {
      key: 'toggle-status',
      label: isActive ? 'Remove' : 'Restore',
      danger: isActive,
      onClick: () => (isActive ? onRemove(record) : confirmRestore()),
    },
  ]

  // The row itself opens the profile, so the trigger must not bubble.
  const stopRowClick = (event: React.MouseEvent | React.KeyboardEvent) =>
    event.stopPropagation()

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <KebabButton
        type="button"
        aria-label={`Actions for ${record.clientName}`}
        onClick={stopRowClick}
        onKeyDown={stopRowClick}
      >
        <MoreOutlined />
      </KebabButton>
    </Dropdown>
  )
}
